import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import { AddCircleOutline, DeleteOutline, PlayArrow, SwapHoriz } from '@mui/icons-material';
import StopMotionImage from 'components/play/StopMotionImage';
import { muscleLabel, exerciseTypeLabel } from 'components/play/constants';

/**
 * OTRIX Play — fila de ejercicio de "Mi rutina": índice + stop-motion + nombre + chips
 * (tipo / músculo) + series·reps + "Ver técnica". Una sola receta para las dos secciones
 * del día: `routine` (lo que ya tienes: mover / quitar) y `suggestion` (recomendados: Agregar).
 */
function RecommendedExerciseRow({
  index,
  exercise,
  sets = null,
  reps = null,
  mode,
  onViewTechnique,
  onAdd = undefined,
  onMove = undefined,
  onRemove = undefined
}) {
  const { t } = useTranslation(['play']);
  const isSuggestion = mode === 'suggestion';

  const metaLine = [
    sets ? `${sets} ${t('card.series', { ns: 'play' })}` : null,
    reps ? `${reps} ${t('card.reps', { ns: 'play' })}` : null
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    // A 320 el índice (16) + la miniatura (64) + las acciones dejaban ~150px al bloque central:
    // en xs la fila envuelve y las acciones bajan a su propia línea.
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        gap: { xs: 1, sm: 1.5 },
        p: { xs: 1, sm: 1.25 },
        borderRadius: '14px',
        bgcolor: 'surface.raised',
        border: '1px solid',
        borderColor: 'hairline.main',
        transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), background-color 0.2s, border-color 0.2s',
        '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover', borderColor: 'hairline.strong' }
      }}>
      <Typography
        variant="caption"
        fontWeight={800}
        sx={{ width: 16, flex: 'none', textAlign: 'center', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
        {index}
      </Typography>

      <Box sx={{ width: 64, flex: 'none', borderRadius: '10px', overflow: 'hidden', border: '1px solid', borderColor: 'hairline.main' }}>
        <StopMotionImage frame1={exercise.image_url_1} frame2={exercise.image_url_2} alt={exercise.localized_name} height={72} />
      </Box>

      <Box sx={{ minWidth: 0, flex: 1, flexBasis: { xs: '55%', sm: 'auto' } }}>
        <Typography variant="body2" fontWeight={800} noWrap>
          {exercise.localized_name}
        </Typography>

        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          {exercise.exercise_type && (
            <Chip size="small" variant="outlined" label={exerciseTypeLabel(t, exercise.exercise_type)} sx={{ height: 20 }} />
          )}
          {exercise.muscle_group && (
            <Chip size="small" label={muscleLabel(t, exercise.muscle_group)} sx={{ bgcolor: 'gold.soft', color: 'gold.main', height: 20 }} />
          )}
          {metaLine && (
            <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {metaLine}
            </Typography>
          )}
        </Stack>

        <Button
          size="small"
          startIcon={<PlayArrow sx={{ fontSize: 16 }} />}
          onClick={() => onViewTechnique(exercise)}
          sx={{ mt: 0.25, ml: -0.75, color: 'gold.main', textTransform: 'none', fontWeight: 700, minHeight: 0, py: 0.25 }}>
          {t('card.viewTechnique', { ns: 'play' })}
        </Button>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{ flex: 'none', ml: { xs: 'auto', sm: 0 } }}>
        {isSuggestion ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />}
            onClick={() => onAdd(exercise)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: 'gold.main',
              borderColor: 'hairline.strong',
              '&:hover': { borderColor: 'gold.main', bgcolor: 'gold.soft' },
              '&:active': { transform: 'scale(0.98)' }
            }}>
            {t('card.add', { ns: 'play' })}
          </Button>
        ) : (
          <>
            <IconButton size="small" aria-label={t('routine.moveAria', { ns: 'play' })} onClick={(e) => onMove(e, exercise)}>
              <SwapHoriz fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              aria-label={t('routine.removeAria', { ns: 'play' })}
              onClick={() => onRemove(exercise)}
              sx={{ color: 'sessionStatus.absent.fg' }}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>
    </Box>
  );
}

RecommendedExerciseRow.propTypes = {
  index: PropTypes.number.isRequired,
  exercise: PropTypes.shape({
    id: PropTypes.string,
    localized_name: PropTypes.string,
    muscle_group: PropTypes.string,
    exercise_type: PropTypes.string,
    image_url_1: PropTypes.string,
    image_url_2: PropTypes.string
  }).isRequired,
  sets: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  reps: PropTypes.string,
  mode: PropTypes.oneOf(['routine', 'suggestion']).isRequired,
  onViewTechnique: PropTypes.func.isRequired,
  /** mode="suggestion" */
  onAdd: PropTypes.func,
  /** mode="routine" — onMove recibe (event, exercise) para anclar el menú de días. */
  onMove: PropTypes.func,
  onRemove: PropTypes.func
};

export default RecommendedExerciseRow;
